import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { LeadMagnetCapture, MAGNETS } from '../home/LeadMagnetCapture';
import { TOKENS } from '../../lib/designTokens';

const STORAGE_KEY = 'nin_exit_intent_dismissed_v1';

// Don't even arm the listener until the user has been on the page for this
// long AND has scrolled at least this far. Tunable from one place.
const ARM_DELAY_MS = 25_000; // 25 seconds of dwell time
const MIN_SCROLL_PX = 400;   // require some scroll engagement
const MOBILE_IDLE_MS = 90_000;

// CRO Audit §3.3 #1 — Exit-intent lead capture.
// Behavior contract:
//   • Shown at most ONCE per browser (localStorage flag).
//   • Never fires in the first ARM_DELAY_MS after mount.
//   • Desktop: requires the user to scroll past MIN_SCROLL_PX first, then
//     fires when the cursor exits the viewport from the top.
//   • Mobile: fires after MOBILE_IDLE_MS of inactivity.
//   • Once dismissed, the listener is detached immediately and the flag is
//     persisted — it will NEVER re-open in the same browser.
export const ExitIntentModal = () => {
  const [open, setOpen] = useState(false);
  // Mirror the "already shown or dismissed" state into a ref so handlers
  // bound via addEventListener never read a stale closure value.
  const firedRef = useRef(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') {
        firedRef.current = true;
        return undefined;
      }
    } catch (err) {
      console.error('Exit-intent storage read failed:', err);
    }

    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

    let armTimer;
    let mobileTimer;
    let removeListener = () => {};

    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      try { localStorage.setItem(STORAGE_KEY, '1'); }
      catch (err) { console.error('Exit-intent storage write failed:', err); }
      setOpen(true);
      removeListener();
    };

    const arm = () => {
      if (firedRef.current) return;
      if (isMobile) {
        mobileTimer = setTimeout(fire, MOBILE_IDLE_MS);
        removeListener = () => { if (mobileTimer) clearTimeout(mobileTimer); };
        return;
      }
      // Desktop: require a small amount of scroll engagement before treating
      // a top-edge mouseout as an "exit intent" (otherwise hovering over the
      // URL bar at page-load triggers it).
      const handler = (e) => {
        if (e.clientY > 0) return;
        if (window.scrollY < MIN_SCROLL_PX) return;
        fire();
      };
      document.addEventListener('mouseout', handler);
      removeListener = () => document.removeEventListener('mouseout', handler);
    };

    armTimer = setTimeout(arm, ARM_DELAY_MS);

    return () => {
      if (armTimer) clearTimeout(armTimer);
      removeListener();
    };
  }, []);

  const dismiss = () => {
    firedRef.current = true;
    try { localStorage.setItem(STORAGE_KEY, '1'); }
    catch (err) { console.error('Exit-intent storage write failed:', err); }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      data-testid="exit-intent-modal"
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute -top-3 -right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full"
          style={{ backgroundColor: TOKENS.surface2, border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="exit-intent-close"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-zinc-300" />
        </button>
        <LeadMagnetCapture magnet={MAGNETS[0]} variant="modal" />
      </div>
    </div>
  );
};
